// src/utils/authHelpers.ts

import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/* ============================================================
   ALTA DE USUARIO
============================================================ */
export async function signUpUser(email: string, password: string) {
  const cleanEmail = email.trim().toLowerCase();

  const { data, error } = await supabase.auth.signUp({
    email: cleanEmail,
    password,
  });

  if (error) {
    Sentry.captureException(error, { extra: { email: cleanEmail } });
    toast.error('Error creando usuario: ' + error.message);
    return { ok: false, error };
  }

  return { ok: true, user: data.user };
}

/* ============================================================
   LOGIN
============================================================ */
export async function loginUser(email: string, password: string) {
  const cleanEmail = email.trim().toLowerCase();

  const { data, error } = await supabase.auth.signInWithPassword({
    email: cleanEmail,
    password,
  });

  if (error || !data.user) {
    if (error) Sentry.captureException(error, { extra: { email: cleanEmail } });
    toast.error('Error al iniciar sesión automáticamente.');
    return { ok: false, user: null };
  }

  return { ok: true, user: data.user };
}

/* ============================================================
   UPDATE METADATA
============================================================ */
export async function updateUserMetadata(metadata: Record<string, any>) {
  const { error } = await supabase.auth.updateUser({ data: metadata });

  if (error) {
    Sentry.captureException(error);
    return { ok: false };
  }

  return { ok: true };
}

/* ============================================================
   PERFIL DE USUARIO
============================================================ */
export async function addPerfil(userId: string, nombrePerfil: string) {
  const { data: perfil, error: perfilError } = await supabase
    .from('perfiles')
    .select('id_perfil')
    .eq('perfil', nombrePerfil)
    .maybeSingle();

  if (perfilError || !perfil) {
    if (perfilError)
      Sentry.captureException(perfilError, { extra: { userId, nombrePerfil } });
    return { ok: false, error: 'Perfil no encontrado' };
  }

  // Se leen activo y fecha_baja, no solo la existencia de la fila: si el
  // usuario dio de baja este perfil, la fila SIGUE ahí con activo = false.
  // Sin esta comprobación el registro no insertaba (la fila existe) ni
  // reactivaba, y el usuario se quedaba sin perfil de forma silenciosa.
  const { data: existing } = await supabase
    .from('usuarios_perfiles')
    .select('id_usuario, activo, fecha_baja')
    .eq('id_usuario', userId)
    .eq('id_perfil', perfil.id_perfil)
    .maybeSingle();

  if (!existing) {
    const { error } = await supabase.from('usuarios_perfiles').insert({
      id_usuario: userId,
      id_perfil: perfil.id_perfil,
      usuario_creacion: userId,
    });

    if (error) {
      Sentry.captureException(error, {
        extra: { userId, idPerfil: perfil.id_perfil },
      });
      return { ok: false };
    }

    return { ok: true, reactivado: false };
  }

  if (existing.activo === false) {
    // fecha_baja marca que la baja fue voluntaria. Si no la lleva, el perfil
    // lo desactivó la empresa (caso trabajador) y no se toca: eso lo tiene
    // que reactivar la empresa, no el propio usuario.
    if (!existing.fecha_baja) {
      return { ok: false, error: 'PERFIL_DESACTIVADO_POR_EMPRESA' };
    }

    const { data: reactivado, error: rpcError } = await supabase.rpc(
      'reactivar_mi_perfil',
      { p_id_perfil: perfil.id_perfil }
    );

    if (rpcError || reactivado !== true) {
      if (rpcError)
        Sentry.captureException(rpcError, {
          extra: { userId, idPerfil: perfil.id_perfil },
        });
      return { ok: false, error: 'No se pudo reactivar el perfil' };
    }

    return { ok: true, reactivado: true };
  }

  return { ok: true, reactivado: false };
}

/* ============================================================
   CONSENTIMIENTO: EMPRESA
============================================================ */
export async function addConsentimientoEmpresa(
  idEmpresa: string,
  version: string,
  texto: string,
  opciones?: {
    privacidad?: boolean;
    terminos?: boolean;
    comunicaciones?: boolean;
  }
) {
  const payload = {
    id_empresa: idEmpresa,
    policy_version: version,
    policy_text: texto,
    acepta_politica_privacidad: opciones?.privacidad ?? true,
    acepta_terminos: opciones?.terminos ?? true,
    acepta_comunicaciones: opciones?.comunicaciones ?? false,
  };

  const { error } = await supabase
    .from('empresa_consentimientos')
    .insert(payload);

  if (error) {
    Sentry.captureException(error, { extra: { idEmpresa, version } });
    toast.error('No se pudo guardar el consentimiento de empresa.');
    return false;
  }

  return true;
}

/* ============================================================
   CONSENTIMIENTO: USUARIO (SIN CUENTA)
============================================================ */
export async function addConsentimientoUsuario(
  idEmpresa: string,
  email: string,
  telefono: string | null,
  version: string,
  texto: string,
  opciones?: {
    tratamiento?: boolean;
    comunicaciones?: boolean;
  }
) {
  const payload = {
    id_empresa: idEmpresa,
    email: email.trim().toLowerCase(),
    telefono: telefono?.trim() || null,
    policy_version: version,
    policy_text: texto,
    acepta_tratamiento: opciones?.tratamiento ?? true,
    acepta_comunicaciones: opciones?.comunicaciones ?? false,
  };

  const { error } = await supabase
    .from('usuario_consentimientos')
    .insert(payload);

  if (error) {
    Sentry.captureException(error, { extra: { idEmpresa, version } });
    toast.error('No se pudo guardar el consentimiento del usuario.');
    return false;
  }

  return true;
}

/* ============================================================
   CONSENTIMIENTO: CLIENTE (CON CUENTA)
============================================================ */
export async function addConsentimientoCliente(
  idUsuario: string,
  email: string,
  telefono: string | null,
  version: string,
  texto: string,
  opciones?: {
    tratamiento?: boolean;
    comunicaciones?: boolean;
  }
) {
  const payload = {
    id_usuario: idUsuario,
    email: email.trim().toLowerCase(),
    telefono: telefono?.trim() || null,
    policy_version: version,
    policy_text: texto,
    acepta_tratamiento: opciones?.tratamiento ?? true,
    acepta_comunicaciones: opciones?.comunicaciones ?? false,
    usuario_creacion: idUsuario,
  };

  const { error } = await supabase
    .from('cliente_consentimientos')
    .insert(payload);

  if (error) {
    Sentry.captureException(error, { extra: { idUsuario, version } });
    toast.error('No se pudo guardar el consentimiento del cliente.');
    return false;
  }

  return true;
}